import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  WatercraftHelmet,
  WatercraftSpinner,
  WatercraftWordmark,
} from '../../features/watercraft';
import {
  useCreatePassCheckoutSessionMutation,
  useGetAccessEntitlementsQuery,
  useGetPassPricesQuery,
} from '../../features/watercraft/watercraftPassesSlice';
import { useIsLoggedInQuery } from '../../features/users/usersSlice';
import { formatAsPounds } from '../../utils/payment-utils';
import { notification } from '../../utils/messaging';
import {
  WATERCRAFT_LOGIN_ROUTE,
  WATERCRAFT_MANAGE_ROUTE,
  WATERCRAFT_PASSES_ROUTE,
  WATERCRAFT_PASSES_SUCCESS_ROUTE,
} from './constants';

export default function WatercraftPassesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: isLoggedIn, isFetching: loadingLogin } = useIsLoggedInQuery({
    refetchOnMountOrArgChange: true,
  });
  const { data: prices, isFetching: loadingPrices } = useGetPassPricesQuery();
  const { data: entitlements, isFetching: loadingEntitlements } =
    useGetAccessEntitlementsQuery(undefined, {
      skip: !isLoggedIn,
      refetchOnMountOrArgChange: true,
    });
  const [createPassCheckoutSession] = useCreatePassCheckoutSessionMutation();
  const [submittingPriceId, setSubmittingPriceId] = useState<string | null>(
    null
  );

  // Set when a guest is bounced through login so we can resume the specific
  // pack's checkout automatically on return instead of a second click.
  const autoBuyId = searchParams.get('autoBuy');
  const autoBought = useRef(false);

  const packs = [...(prices?.prices ?? [])].sort(
    (a, b) => a.pass_count - b.pass_count
  );
  const available = entitlements?.session_pass_counts?.available ?? 0;

  const startCheckout = useCallback(
    (priceId: string) => {
      setSubmittingPriceId(priceId);
      const origin = `${window.location.protocol}//${window.location.host}`;
      createPassCheckoutSession({
        price_id: priceId,
        success_url: `${origin}${WATERCRAFT_PASSES_SUCCESS_ROUTE}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}${WATERCRAFT_PASSES_ROUTE}`,
      })
        .unwrap()
        .then((redirectUrl) => {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            setSubmittingPriceId(null);
          }
        })
        .catch(() => {
          setSubmittingPriceId(null);
          notification.error({
            description: 'Unable to start checkout. Please try again.',
          });
        });
    },
    [createPassCheckoutSession]
  );

  const handleBuy = (priceId: string) => {
    if (!isLoggedIn) {
      const returnTo = `${WATERCRAFT_PASSES_ROUTE}?autoBuy=${encodeURIComponent(
        priceId
      )}`;
      navigate(
        `${WATERCRAFT_LOGIN_ROUTE}?redirect=${encodeURIComponent(returnTo)}`
      );
      return;
    }
    startCheckout(priceId);
  };

  useEffect(() => {
    if (
      !autoBuyId ||
      !isLoggedIn ||
      loadingPrices ||
      loadingEntitlements ||
      entitlements?.subscription?.has_access ||
      autoBought.current
    ) {
      return;
    }
    const pack = (prices?.prices ?? []).find((p) => p.id === autoBuyId);
    if (!pack) {
      return;
    }
    autoBought.current = true;
    startCheckout(autoBuyId);
  }, [
    autoBuyId,
    isLoggedIn,
    loadingPrices,
    loadingEntitlements,
    entitlements,
    prices,
    startCheckout,
  ]);

  if (loadingLogin || loadingPrices || (isLoggedIn && loadingEntitlements)) {
    return (
      <div className='grid min-h-screen w-full place-content-center bg-watercraft-bg text-watercraft-text-primary'>
        <WatercraftHelmet />
        <WatercraftSpinner />
      </div>
    );
  }

  if (entitlements?.subscription?.has_access) {
    return <Navigate to={WATERCRAFT_MANAGE_ROUTE} replace />;
  }

  return (
    <main className='flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-gradient-to-b from-watercraft-bg to-watercraft-surface p-6 text-watercraft-text-primary'>
      <WatercraftHelmet />
      <WatercraftWordmark className='h-9 w-auto text-watercraft-text-primary' />
      <section className='w-full max-w-sm text-center'>
        <h1 className='text-2xl font-bold'>Session passes</h1>
        {isLoggedIn && (
          <p className='mt-2 text-sm text-watercraft-text-secondary'>
            You have {available} session pass{available === 1 ? '' : 'es'}{' '}
            available.
          </p>
        )}
        {packs.length ? (
          <div className='mt-6 flex flex-col gap-3'>
            {packs.map((pack) => (
              <div
                key={pack.id}
                className='flex items-center justify-between gap-4 rounded-xl border border-watercraft-border bg-watercraft-surface p-4 text-left'
              >
                <div>
                  <p className='text-lg font-bold'>
                    {pack.pass_count} pass{pack.pass_count === 1 ? '' : 'es'}
                  </p>
                  <p className='text-sm text-watercraft-text-secondary'>
                    {pack.cost < pack.original_cost && (
                      <del className='mr-1 text-watercraft-text-muted'>
                        {formatAsPounds(pack.original_cost)}
                      </del>
                    )}
                    {formatAsPounds(pack.cost)}
                  </p>
                </div>
                <Button
                  type='primary'
                  className='h-11 rounded-lg border-none bg-watercraft-primary px-6 text-base font-semibold text-watercraft-text-primary hover:bg-watercraft-primary-light'
                  disabled={submittingPriceId !== null}
                  onClick={() => handleBuy(pack.id)}
                >
                  {submittingPriceId === pack.id ? (
                    <WatercraftSpinner className='h-5 w-5 border-watercraft-text-primary/30 border-t-watercraft-text-primary' />
                  ) : (
                    'Buy'
                  )}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className='mt-6 text-sm text-watercraft-text-secondary'>
            No packs available right now.
          </p>
        )}
      </section>
    </main>
  );
}
